import { CATEGORIES } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const emptyProductForm = { name: "", category: "kids", type: "", price: "", images: "", sizes: "", colours: "", description: "", featured: false };

export const productToForm = (p) => ({
  name: p.name,
  category: p.category,
  type: p.type,
  price: String(p.price),
  images: (p.images || []).join(", "),
  sizes: (p.sizes || []).join(", "),
  colours: (p.colours || []).map((c) => (c.image ? `${c.name} | ${c.image}` : c.name)).join("\n"),
  description: p.description || "",
  featured: p.featured || false,
  in_stock: p.in_stock ?? true,
});

export const ProductFormDialog = ({ open, onOpenChange, editing, form, setForm, onSave, saving }) => {
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input data-testid="product-form-name" value={form.name} onChange={set("name")} className="mt-1 bg-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="product-form-category" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Input data-testid="product-form-type" value={form.type} onChange={set("type")} placeholder="T-Shirt" className="mt-1 bg-white" />
            </div>
          </div>
          <div>
            <Label>Price (R)</Label>
            <Input data-testid="product-form-price" type="number" value={form.price} onChange={set("price")} className="mt-1 bg-white" />
          </div>
          <div>
            <Label>Image URLs (comma separated)</Label>
            <Textarea data-testid="product-form-images" value={form.images} onChange={set("images")} placeholder="https://... , https://..." className="mt-1 bg-white" />
          </div>
          <div>
            <Label>Sizes (comma separated)</Label>
            <Input data-testid="product-form-sizes" value={form.sizes} onChange={set("sizes")} placeholder="5Y, 6Y, 7Y" className="mt-1 bg-white" />
          </div>
          <div>
            <Label>Colours (one per line: Colour name | optional image URL)</Label>
            <Textarea data-testid="product-form-colours" value={form.colours} onChange={set("colours")} placeholder={"Brown & White | /api/static/products/hd/CKC-023-1.jpg\nPink & White"} className="mt-1 bg-white" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea data-testid="product-form-description" value={form.description} onChange={set("description")} className="mt-1 bg-white" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="product-form-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
            Featured on homepage
          </label>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full border border-purple-200 font-medium">Cancel</button>
          <button data-testid="product-form-save" onClick={onSave} disabled={saving} className="px-5 py-2 rounded-full brand-gradient text-white font-semibold disabled:opacity-50">
            {saving ? "Saving..." : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
